import { Component, OnInit, inject, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import * as QRCode from 'qrcode';
import { FacturacionService, Factura } from '../../core/services/facturacion.service';
import { MetaService } from '../../core/services/meta.service';

@Component({
  selector: 'app-print-invoice',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './print-invoice.html',
  styleUrls: ['./print-invoice.css']
})
export class PrintInvoiceComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private facturacionService = inject(FacturacionService);
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);
  private metaService = inject(MetaService);
  private isBrowser: boolean;

  factura: Factura | null = null;
  isLoading = true;
  errorMessage: string | null = null;
  qrImages: Map<string, string> = new Map(); // ticketid → data URL

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.metaService.resetToDefault();

    const facturaid = this.route.snapshot.paramMap.get('facturaid');
    
    if (!facturaid) {
      console.error('❌ No se proporcionó facturaid');
      this.errorMessage = 'ID de factura no válido';
      this.goBackAfterDelay();
      return;
    }

    console.log('🔍 Cargando factura:', facturaid);
    this.loadInvoiceAndPrint(facturaid);
  }

  loadInvoiceAndPrint(facturaid: string): void {
    this.facturacionService.getInvoiceDetails(facturaid).subscribe({
      next: (response) => {
        console.log('✅ Respuesta recibida:', response);
        if (response.success) {
          this.factura = response.data;
          this.isLoading = false;
          console.log('✅ Factura cargada correctamente, mostrando vista de impresión');
          this.generarQRs();
        } else {
          console.error('❌ Error en respuesta:', response.message);
          this.errorMessage = response.message;
          this.isLoading = false;
          this.goBackAfterDelay();
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error al cargar factura:', error);
        this.errorMessage = 'Error al cargar la factura. Por favor, intenta nuevamente.';
        this.isLoading = false;
        this.goBackAfterDelay();
        this.cdr.detectChanges();
      }
    });
  }

  async generarQRs(): Promise<void> {
    if (!this.factura?.tickets?.length) {
      return;
    }

    const qrEntries = await Promise.all(
      this.factura.tickets.map(async (ticket) => {
        try {
          const qrImage = await QRCode.toDataURL(ticket.qr, {
            width: 220,
            margin: 1
          });
          return [ticket.ticketid, qrImage] as const;
        } catch (error) {
          console.error('❌ Error generando QR para ticket', ticket.ticketid, error);
          return [ticket.ticketid, ''] as const;
        }
      })
    );

    this.qrImages = new Map(qrEntries.filter(([, image]) => image));
    this.cdr.detectChanges();
  }

  goBack(): void {
    if (this.isBrowser) {
      window.close();
      // Si no se puede cerrar la ventana, navegar de vuelta
      setTimeout(() => {
        this.router.navigate(['/profile']);
      }, 100);
    }
  }

  goBackAfterDelay(): void {
    if (this.isBrowser) {
      setTimeout(() => {
        this.goBack();
      }, 3000);
    }
  }

  /** Busca el TicketDetalle enriquecido que corresponde a un ticketid del array items */
  getTicketDetalle(ticketid: string) {
    return this.factura?.tickets?.find(t => t.ticketid === ticketid) ?? null;
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      
      return date.toLocaleDateString('es-EC', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'America/Guayaquil'
      });
    } catch {
      return 'N/A';
    }
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      
      return date.toLocaleString('es-EC', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Guayaquil'
      });
    } catch {
      return 'N/A';
    }
  }
}
